/**
 * The play screen: full-viewport mandala + pointer layer + glass chrome; owns the rAF loop that feeds the renderer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { pitchSpecHz, saHz, scaleById, type Family } from '@tonist/shared';
import { getEngine } from '../engine';
import { createMandalaRenderer } from './renderer';
import { MandalaView } from './MandalaView';
import { MandalaLabels } from './MandalaLabels';
import { layoutFor, type RingIndex } from './geometry';
import type { SceneState, ZoneActivity } from './renderer-api';
import { PointerLayer } from './pointer';
import { engineStarted, ensureEngineStarted, onEngineStarted, type EngineWithExtras, type RendererWithExtras } from '../lib/engineBoot';
import { audioDebugEnabled, audioLog, debugRequestedByEnvironment, setAudioDebug, setRemoteLogging } from '../engine/log';
import { useSettings, resolvedTheme, prefersReducedMotion } from '../store/settingsStore';
import { useDrones } from '../store/dronesStore';
import { useAuth, initials } from '../store/authStore';
import { useUi, toast } from '../store/uiStore';
import { tuningFromSettings, scaleMask } from '../music/tuning';
import { activeDroneIds, droneIdForZone, ringFamilies } from '../music/assignment';
import { Wordmark } from '../components/Wordmark';
import { Toasts } from '../components/Toasts';
import { DroneSelector } from '../components/play/DroneSelector';
import { TuneSheet } from '../components/play/TuneSheet';
import { ZoneButtons } from '../components/play/ZoneButtons';
import { ShortcutsHint } from '../components/play/ShortcutsHint';
import { LoadingIndicator } from '../components/play/LoadingIndicator';
import { VOICE_CAP } from '../music/voiceRules';

const WAVE = 256;

export function PlayScreen() {
  const engine = useMemo<EngineWithExtras>(() => getEngine(), []);
  const rendererRef = useRef<RendererWithExtras | null>(null);
  if (!rendererRef.current) rendererRef.current = createMandalaRenderer() as RendererWithExtras;
  const renderer = rendererRef.current;

  const settings = useSettings();
  const tonicPc = settings.tonicPc, scaleId = settings.scaleId, ji = settings.ji, mode = settings.mode;
  const assignment = settings.assignment, droneIds = settings.droneIds, calm = settings.calm, theme = settings.theme, volume = settings.volume;
  const byId = useDrones((s) => s.byId);
  const presets = useDrones((s) => s.presets);
  const mine = useDrones((s) => s.mine);
  const user = useAuth((s) => s.user);
  const panel = useUi((s) => s.panel);
  const setPanel = useUi((s) => s.setPanel);
  const togglePanel = useUi((s) => s.togglePanel);

  const [layout, setLayout] = useState(() => layoutFor(window.innerWidth, window.innerHeight));
  const tuning = useMemo(() => tuningFromSettings({ tonicPc, scaleId, ji }), [tonicPc, scaleId, ji]);
  const [voiceCount, setVoiceCount] = useState(0);
  const [started, setStarted] = useState(engineStarted());
  const surfaceRef = useRef<HTMLDivElement>(null);

  // latest values for the pointer layer (which is created once)
  const latest = useRef({ layout, tuning, settings });
  latest.current = { layout, tuning, settings };
  const familyOf = useCallback((id: string): Family => byId(id)?.family ?? 'white', [byId]);
  const familyRef = useRef(familyOf); familyRef.current = familyOf;

  const pointer = useMemo(() => new PointerLayer({
    engine,
    getRenderer: () => rendererRef.current,
    getLayout: () => latest.current.layout,
    getTuning: () => latest.current.tuning,
    getSettings: () => latest.current.settings,
    familyOf: (id) => familyRef.current(id),
    onFirstTouch: () => { setStarted(true); },
    setTonic: (pc) => useSettings.getState().setTonic(pc),
    stepTonic: (dir, bySemitone) => { useSettings.getState().stepTonic(dir, bySemitone); rendererRef.current?.pulse('tonic-change'); },
    stepScale: (dir) => useSettings.getState().stepScale(dir),
    selectScaleIndex: (i) => useSettings.getState().selectScaleIndex(i),
    toggleMode: () => useSettings.getState().toggleMode(),
    onMacroNudge: (kind, delta) => { if (kind === 'brightness') useSettings.getState().nudgeBrightness(delta); else useSettings.getState().nudgeWidth(delta); },
  }), [engine]);

  // attach pointer layer
  useEffect(() => {
    const el = surfaceRef.current; if (!el) return;
    pointer.attach(el);
    return () => pointer.dispose();
  }, [pointer]);

  // layout on resize
  useEffect(() => {
    const onResize = () => setLayout(layoutFor(window.innerWidth, window.innerHeight));
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); window.visualViewport?.removeEventListener('resize', onResize); };
  }, []);

  // scene → renderer
  useEffect(() => {
    const scene: SceneState = {
      degrees: tuning.scale.semitones.length,
      tonicPc,
      scaleMask: scaleMask(tuning.scale, tonicPc),
      ringFamilies: ringFamilies({ assignment, droneIds }, familyOf),
      calm: calm || prefersReducedMotion(),
      theme: resolvedTheme(theme),
      mode,
    };
    renderer.setScene(scene);
  }, [renderer, tuning, tonicPc, assignment, droneIds, calm, theme, mode, familyOf, presets, mine]);

  // tuning changes retune persistent voices (lowest first, staggered) and the implied tanpura
  const firstTuning = useRef(true);
  useEffect(() => {
    if (firstTuning.current) { firstTuning.current = false; return; }
    pointer.retuneAll(true);
  }, [pointer, tuning]);
  useEffect(() => { if (started) engine.setTonicHz(saHz(tonicPc)); }, [engine, started, tonicPc]);
  const debugAudio = useSettings((s) => s.debugAudio);
  useEffect(() => { const on = debugAudio || debugRequestedByEnvironment(); setAudioDebug(on); setRemoteLogging(on); }, [debugAudio]);
  const pitchEngine = useSettings((s) => s.pitchEngine);
  useEffect(() => { engine.setPitchMode?.(pitchEngine); }, [engine, pitchEngine]);
  // choosing another drone while voices are latched or continuous moves them to the new drone
  const firstAssignment = useRef(true);
  useEffect(() => {
    if (firstAssignment.current) { firstAssignment.current = false; return; }
    if (started) pointer.migrateDrones();
  }, [pointer, started, assignment, droneIds]);
  useEffect(() => { if (started) engine.setMasterVolume(volume); }, [engine, started, volume]);
  useEffect(() => onEngineStarted(() => setStarted(true)), []);

  // mode switch clears continuous voices / latched state
  const firstMode = useRef(true);
  useEffect(() => { if (firstMode.current) { firstMode.current = false; return; } pointer.releaseAll(); }, [pointer, mode]);

  // load drones the current assignment needs. Before the engine has started this only prefetches the sample bytes and
  // queues the decode, which start() then awaits, so the very first touch plays the real samples.
  const loaded = useRef(new Set<string>());
  useEffect(() => {
    for (const id of activeDroneIds({ assignment, droneIds })) {
      if (loaded.current.has(id)) continue;
      const drone = byId(id);
      if (!drone) continue;
      loaded.current.add(id);
      toast(`preparing ${drone.name}`);
      engine.loadDrone(drone).catch((e: unknown) => { loaded.current.delete(id); toast(`couldn't load ${drone.name}: ${(e as Error).message}`, 'error'); });
    }
  }, [engine, started, assignment, droneIds, byId]);

  // wheel macro nudges → live drone update (brightness / width), when the engine supports it
  const brightnessNudge = settings.brightnessNudge, widthNudge = settings.widthNudge;
  const lastToast = useRef(0);
  useEffect(() => {
    if (!started || (brightnessNudge === 0 && widthNudge === 0)) return;
    const base = byId(droneIds[0] ?? 'sa'); if (!base) return;
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
    const live = { ...base, macros: { ...base.macros, brightness: clamp(base.macros.brightness + brightnessNudge, 0, 1) }, space: { ...base.space, width: clamp(base.space.width + widthNudge, 0, 2) } };
    engine.updateDrone?.(live);
    const now = performance.now();
    if (now - lastToast.current > 400) { lastToast.current = now; toast(`brightness ${Math.round(live.macros.brightness * 100)}% · width ${live.space.width.toFixed(2)}`); }
  }, [engine, started, brightnessNudge, widthNudge, droneIds, byId]);

  // rAF loop: engine → renderer. React never renders per frame.
  useEffect(() => {
    const buf = new Float32Array(WAVE);
    const spec = new Uint8Array(64);
    const amps = new Map<number, number>();
    let raf = 0, last = performance.now(), lastDraw = 0, lastCount = -1, lastCountAt = 0;
    const stats = { n: 0, sum: 0, max: 0, long: 0, at: 0 };
    const loop = (t: number) => {
      raf = requestAnimationFrame(loop);
      const rawMs = t - last;
      const dt = Math.min(0.1, rawMs / 1000); last = t;
      // debug health: frame pacing on the main thread (long frames starve the pointer/gesture path, and on a
      // software-rendered GPU they starve the whole machine, audio thread included)
      if (audioDebugEnabled()) {
        stats.n++; stats.sum += rawMs; if (rawMs > stats.max) stats.max = rawMs; if (rawMs > 50) stats.long++;
        if (stats.at === 0) stats.at = t;
        else if (t - stats.at >= 5000) {
          const d = rendererRef.current?.diagnostics?.();
          audioLog.log('context', `frames: ${stats.n} in ${((t - stats.at) / 1000).toFixed(1)} s, avg ${(stats.sum / stats.n).toFixed(1)} ms, max ${stats.max.toFixed(0)} ms, ${stats.long} over 50 ms${d ? `; renderer tier ${d.tier}, dpr ${d.dpr.toFixed(2)}, ${Math.round(d.cssWidth)}×${Math.round(d.cssHeight)} css px, gpu "${d.gpu}"` : ''}`);
          stats.n = 0; stats.sum = 0; stats.max = 0; stats.long = 0; stats.at = t;
        }
      } else stats.at = 0;
      const sounding = pointer.tick(t, dt);
      if (t - lastCountAt > 200) { lastCountAt = t; if (sounding !== lastCount) { lastCount = sounding; setVoiceCount(sounding); } }
      const idle = pointer.voices.size === 0 && pointer.activePointerCount === 0;
      if (idle && t - lastDraw < 33) return; // 30 fps when nothing sounds
      lastDraw = t;
      const r = rendererRef.current; if (!r) return;
      const zones: ZoneActivity[] = [];
      for (const v of pointer.voices.values()) {
        let target = 0;
        if (engine.getVoiceWaveform(v.id, buf)) {
          r.setWaveform(v.slot, buf);
          let sum = 0; for (let i = 0; i < WAVE; i++) sum += buf[i]! * buf[i]!;
          target = Math.min(1, Math.sqrt(sum / WAVE) * 3);
        }
        const prev = amps.get(v.id) ?? 0;
        const amp = prev + (target - prev) * (target > prev ? 0.15 : 0.03);
        amps.set(v.id, amp);
        zones.push({ ring: v.ring, index: v.index, slot: v.slot, amplitude: amp, age: (t - v.startTime) / 1000, latched: v.latched || v.held || v.continuous, releasing: v.handle.releasing, family: v.family });
      }
      for (const id of [...amps.keys()]) if (!pointer.voices.has(id)) amps.delete(id);
      r.setZoneActivity(zones);
      r.setMaster(engineStarted() ? engine.getMasterSpectrum(spec) : 0, sounding);
      r.frame(t);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [engine, pointer]);

  // keyboard zone buttons: sound a short note / set the tonic
  const onZone = useCallback((ring: RingIndex, index: number) => {
    const { tuning: tu, settings: st } = latest.current;
    const angle = (index * Math.PI * 2) / (tu.scale.semitones.length + 1);
    ensureEngineStarted(engine).then(() => {
      const hz = pitchSpecHz(tu, { degree: index, octave: ring - 1, bendCents: 0 });
      const droneId = droneIdForZone(st, ring, angle);
      if (st.mode === 'continuous') { engine.setContinuous(droneId, hz, { radius: 0.5, velocity: 0 }); return; }
      const h = engine.noteOn(droneId, hz, { radius: 0.5, velocity: 0 });
      setTimeout(() => h.release(), 900);
    }).catch(() => toast('Audio could not start', 'error'));
  }, [engine]);
  const onTonic = useCallback((pc: number) => pointer.changeTonic(pc), [pointer]);

  const scale = scaleById(scaleId);
  const currentName = byId(droneIds[0] ?? 'sa')?.name ?? droneIds[0];
  const closePanel = useCallback(() => setPanel('none'), [setPanel]);

  return (
    <div className="play">
      <div ref={surfaceRef} className="play__surface" role="application" aria-label="Tonist mandala. Touch or drag to sound a drone; use the zone buttons with a keyboard." tabIndex={0}>
        <MandalaView renderer={renderer} calm={calm || prefersReducedMotion()} />
        <MandalaLabels layout={layout} scale={scale} tonicPc={tonicPc} />
      </div>
      <ZoneButtons layout={layout} scale={scale} tonicPc={tonicPc} onZone={onZone} onTonic={onTonic} />

      <div className="play__chrome">
        <div className="glass glass--pill chrome-tl"><Wordmark /></div>

        <div className="glass glass--pill chrome-tr">
          <div className="seg" role="group" aria-label="Mode">
            <button type="button" aria-pressed={mode === 'play'} aria-label="Play mode" title="Play: a touch sounds, lifting lets it fade" onClick={() => settings.set({ mode: 'play' })}>
              <span className="seg__wide">Play</span><span className="seg__narrow" aria-hidden="true">▸</span>
            </button>
            <button type="button" aria-pressed={mode === 'continuous'} aria-label="Continuous mode" title="Continuous: each drone holds one eternal voice" onClick={() => settings.set({ mode: 'continuous' })}>
              <span className="seg__wide">Continuous</span><span className="seg__narrow" aria-hidden="true">∞</span>
            </button>
          </div>
          <button type="button" className="btn" aria-expanded={panel === 'drones'} aria-haspopup="dialog" onClick={() => togglePanel('drones')} title="Choose drones">
            <span className={`dot dot--${familyOf(droneIds[0] ?? 'sa')}`} aria-hidden="true" />
            <span className="btn__text">{assignment === 'single' ? currentName : `${assignment === 'ring' ? 'By ring' : 'By direction'}`}</span>
          </button>
          <button type="button" className="btn" aria-expanded={panel === 'tune'} aria-haspopup="dialog" onClick={() => togglePanel('tune')} title="Tonic, scale, tuning">
            <span aria-hidden="true">♪</span><span className="btn__text">{scale.raga} · {['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'][tonicPc]}</span>
          </button>
          <VoicesMeter count={voiceCount} />
          <Link to="/drones" className="btn btn--ghost" title="Drones gallery and editor"><span className="btn__text">Drones</span><span aria-hidden="true">✦</span></Link>
          {user
            ? <Link to="/auth" className="avatar is-in" title={user.email} aria-label={`Account: ${user.email}`}>{initials(user.email)}</Link>
            : <Link to="/auth" className="avatar" title="Sign in" aria-label="Sign in">◯</Link>}
        </div>

        <ShortcutsHint />
        <LoadingIndicator />
        <div className="glass glass--pill chrome-br hint">{started ? (mode === 'play' ? 'touch to sound · lift to let it fade' : 'each drone holds one eternal voice') : 'touch the mandala to begin'}</div>

        {panel === 'drones' && <DroneSelector onClose={closePanel} />}
        {panel === 'tune' && <TuneSheet onClose={closePanel} />}
        <Toasts />
      </div>
    </div>
  );
}

function VoicesMeter({ count }: { count: number }) {
  return (
    <div className="voices" role="meter" aria-valuemin={0} aria-valuemax={VOICE_CAP} aria-valuenow={Math.min(VOICE_CAP, count)} aria-label={`${count} voices sounding`} title={`${count} voices`}>
      {Array.from({ length: VOICE_CAP }, (_, i) => <i key={i} className={i < count ? 'on' : ''} />)}
    </div>
  );
}
